// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import SearchBox from "../SearchBox";
import DefaultModal from "../DefaultModal";

// Styles
import {
  CustomTextFieldOutlined,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../styles/muiStylesHelper";

// Utils
import { getAllExamSections } from "../../../utils/examReportsHelper";

const LinkDeviceModalGeneral = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  patientOptions = [],
  examsOptions = [],
  deviceLinkPatient,
  setDeviceLinkPatient,
  deviceLinkExam,
  setDeviceLinkExam,
  loading,
}) => {
  // internationalization
  const { t } = useTranslation();

  const formattedPatientOptions = useMemo(
    () =>
      patientOptions.map((option) => {
        return {
          name: `${option?.firstName} ${option?.lastName}`,
          val: option?.id,
        };
      }),
    [patientOptions]
  );

  const sortedPatientOptions = useMemo(
    () =>
      formattedPatientOptions.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      }),
    [formattedPatientOptions]
  );

  const formattedExamOptions = useMemo(
    () =>
      examsOptions.map((exam) => {
        const examSections = getAllExamSections(exam);
        const examAlgorithm = examSections?.[0]?.algorithm ?? "N/A";
        const examDate = exam.scheduledDate?.slice(0, 10) ?? "N/A";

        const name = `${examAlgorithm} | ${examDate}`;
        return {
          name: name,
          val: exam?.id,
        };
      }),
    [examsOptions]
  );

  const sortedExamOptions = useMemo(
    () =>
      formattedExamOptions.sort((a, b) => {
        return a.name.localeCompare(b.name);
      }),
    [formattedExamOptions]
  );

  const shouldDisable = useMemo(() => {
    let isIncluded = 0;
    formattedPatientOptions.forEach((option) => {
      if (option?.val === deviceLinkPatient?.val) isIncluded++;
    });
    formattedExamOptions.forEach((option) => {
      if (option?.val === deviceLinkExam?.val) isIncluded++;
    });
    return (
      !(deviceLinkExam && deviceLinkPatient) || loading || isIncluded !== 2
    );
  }, [
    deviceLinkExam,
    deviceLinkPatient,
    formattedExamOptions,
    formattedPatientOptions,
    loading,
  ]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("word_link_device")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button variant="text" sx={ModalCustomCancel} onClick={onCancel}>
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            color="primary"
            disabled={shouldDisable}
            onClick={() => onConfirm(deviceLinkExam?.val)}
          >
            {t("button_link")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        {/* Patient selector */}
        <FormControl
          fullWidth
          sx={() => CustomTextFieldOutlined({ my: 0 })}
          color="warning"
        >
          <SearchBox
            data={sortedPatientOptions}
            state={deviceLinkPatient}
            setState={setDeviceLinkPatient}
            propertyBit={4}
            label={t("current_patient")}
          />
        </FormControl>

        {/* Exam selector */}
        <SearchBox
          data={sortedExamOptions}
          state={deviceLinkExam}
          setState={setDeviceLinkExam}
          propertyBit={4}
          label={t("word_exam")}
        />
      </Grid>
    </DefaultModal>
  );
};

export default LinkDeviceModalGeneral;
